<template>
  <div class="tiptap-editor" :class="{ 'tiptap-editor--editable': editable }">
    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5">
          Insert link
        </v-card-title>
        <v-text-field dense v-model="url" label="Web address (URL)" outlined
          prepend-inner-icon="mdi-link"></v-text-field>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog = false">
            Cancel
          </v-btn>
          <v-btn color="green darken-1" text @click="dialog = false, setLink()">
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div v-if="editor" class="editor-wrapper">
      <editor-content :editor="editor" class="editor-content-wrapper" />
      <bubble-menu :editor="editor" :should-show="shouldShowBubbleMenu"
        :tippy-options="{ duration: 100, placement: 'top', offset: [0, 8], zIndex: 1000 }"
        class="bubble-menu-container">
        <v-card class="bubble-menu-card" elevation="4" shaped>
          <v-card-text class="pa-1">
            <!-- Text Formatting Group -->
            <div class="menu-group">
              <v-btn-toggle v-model="toggle_multiple" dense multiple class="menu-group-buttons">
                <v-btn small @click="editor.chain().focus().toggleBold().run()"
                  :class="{ 'is-active': editor.isActive('bold') }" title="Bold">
                  <v-icon small>mdi-format-bold</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleItalic().run()"
                  :class="{ 'is-active': editor.isActive('italic') }" title="Italic">
                  <v-icon small>mdi-format-italic</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleUnderline().run()"
                  :class="{ 'is-active': editor.isActive('underline') }" title="Underline">
                  <v-icon small>mdi-format-underline</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleStrike().run()"
                  :class="{ 'is-active': editor.isActive('strike') }" title="Strikethrough">
                  <v-icon small>mdi-format-strikethrough</v-icon>
                </v-btn>
                <v-btn small @click="editor.chain().focus().toggleCode().run()"
                  :class="{ 'is-active': editor.isActive('code') }" title="Inline Code">
                  <v-icon small>mdi-code-tags</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>

            <!-- Divider -->
            <v-divider vertical class="mx-1"></v-divider>

            <!-- Color and Link Group -->
            <div class="menu-group">
              <v-menu v-model="colorMenuOpen" offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small v-bind="attrs" v-on="on" @mousedown.stop
                    :class="{ 'is-active': editor.getAttributes('textStyle').color }" title="Text Color">
                    <v-icon small>mdi-format-color-text</v-icon>
                  </v-btn>
                </template>
                <v-card>
                  <div v-if="textColorHistory.length" class="text-color-swatches pa-2">
                    <v-tooltip bottom v-for="(hex, i) in textColorHistory" :key="i">
                      <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="color-swatch" :style="{ backgroundColor: hex }" v-bind="attrs"
                          v-on="on" @click="applyRecentColor(hex)" :title="hex">
                        </button>
                      </template>
                      <span>{{ hex }}</span>
                    </v-tooltip>
                  </div>
                  <v-color-picker :value="selectedTextColor" @input="handleColorChange" flat hide-mode-switch
                    mode="hexa">
                  </v-color-picker>
                  <v-card-actions class="pa-2">
                    <v-btn small text @click="resetTextColor">Reset</v-btn>
                  </v-card-actions>
                </v-card>
              </v-menu>
              <v-btn small @click="dialog = true" :class="{ 'is-active': editor.isActive('link') }" title="Add Link">
                <v-icon small>mdi-link</v-icon>
              </v-btn>
              <v-btn small @click="editor.chain().focus().unsetLink().run()" :disabled="!editor.isActive('link')"
                title="Remove Link">
                <v-icon small>mdi-link-off</v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </bubble-menu>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'

export default {
  components: {
    EditorContent,
    BubbleMenu,
  },

  props: {
    value: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true
    },
    sectionType: {
      type: String,
      default: 'body'
    }
  },

  data() {
    return {
      editor: null,
      toggle_multiple: [],
      dialog: false,
      url: "",
      updateTimeout: null,
      lastEmittedContent: null,
      colorChangeTimeout: null,
      colorMenuOpen: false,
      /** Selection range when color menu was opened (from, to); used to close when selection changes. */
      colorMenuOpenSelection: null,
      closeColorMenuTimeout: null,
      selectionUpdateOff: null,
    }
  },

  computed: {
    ...mapState(['textColorHistory']),
    selectedTextColor: function () {
      if (!this.editor) return "#000000";
      let color = this.editor.getAttributes("textStyle").color;
      if (!color) color = "#000000";
      return color;
    },
    selectedBackgroundColor: function () {
      if (!this.editor) return "#ffffff";
      // Check for highlight extension
      if (this.editor.isActive('highlight')) {
        let color = this.editor.getAttributes("highlight").color;
        if (color) return color;
      }
      return "#ffffff";
    }
  },

  watch: {
    colorMenuOpen(isOpen) {
      if (isOpen && this.editor) {
        const { from, to } = this.editor.state.selection
        this.colorMenuOpenSelection = { from, to }
      } else {
        this.colorMenuOpenSelection = null
      }
    },
    editable: {
      handler(editable) {
        if (!editable) this.colorMenuOpen = false
      },
    },
    value(value) {
      if (!this.editor) return;

      // Optimize: Only update if content actually changed
      // Use a more efficient comparison by checking length first
      const currentHtml = this.editor.getHTML();
      if (currentHtml === value) {
        return;
      }

      // Update last emitted content to prevent unnecessary re-emission
      this.lastEmittedContent = value;

      // Clear any pending debounced updates when setting content externally
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }

      this.editor.commands.setContent(value, false)
    },
  },

  mounted() {
    const extensions = [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          ////////////console.log("NODE: ", node);
          if (this.sectionType === 'heading') {
            return 'Enter a section heading...'
          }

          return 'Enter a section description...'
        },
      }),
    ]

    // Add Highlight extension with multicolor support
    // extensions.push(Highlight.configure({ multicolor: true }))

    this.editor = new Editor({
      content: this.value,
      extensions: extensions,
      editable: this.editable,
      onUpdate: () => {
        // Debounce updates to reduce re-renders and memory pressure
        if (this.updateTimeout) {
          clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = setTimeout(() => {
          const html = this.editor.getHTML();
          // Only emit if content actually changed (avoid unnecessary updates)
          if (html !== this.lastEmittedContent) {
            this.lastEmittedContent = html;
            this.$emit('input', { "html": html, "sectionType": this.sectionType });
          }
        }, 300); // 300ms debounce - adjust as needed
      },
    })

    // Color picker may only stay open when there is a selection and it was opened by click.
    // Close in all other cases: no selection, selection changed, or editor not editable.
    const onSelectionUpdate = () => {
      if (!this.editor || !this.editable) {
        this.colorMenuOpen = false
        return
      }
      if (this.closeColorMenuTimeout) {
        clearTimeout(this.closeColorMenuTimeout)
        this.closeColorMenuTimeout = null
      }
      const { from, to, empty } = this.editor.state.selection
      if (empty) {
        this.closeColorMenuTimeout = setTimeout(() => {
          this.colorMenuOpen = false
          this.colorMenuOpenSelection = null
          this.closeColorMenuTimeout = null
        }, 120)
        return
      }
      if (this.colorMenuOpen && this.colorMenuOpenSelection) {
        if (from !== this.colorMenuOpenSelection.from || to !== this.colorMenuOpenSelection.to) {
          this.colorMenuOpen = false
          this.colorMenuOpenSelection = null
        }
      }
    }
    this.editor.on('selectionUpdate', onSelectionUpdate)
    this.selectionUpdateOff = () => this.editor.off('selectionUpdate', onSelectionUpdate)

    // Force Vue to update after editor is created
    this.$nextTick(() => {
      // Editor is now fully initialized
    })
  },

  methods: {
    shouldShowBubbleMenu({ editor, view, state, oldState, from, to }) {
      // Only show bubble menu when text is selected and editor is editable
      if (!editor || !state || !this.editable) {
        return false
      }

      // Check if there's a text selection
      const { selection } = state
      const { empty } = selection

      // Don't show if selection is empty
      if (empty) {
        return false
      }

      // Show if there's a text selection (from and to are different)
      // This ensures we have an actual text selection, not just a cursor position
      return from !== to
    },

    handleColorChange(newColor) {
      // Handle both hex and hexa format
      const color = typeof newColor === 'string' ? newColor : (newColor.hexa || newColor.hex || '#000000');
      
      // Debounce color changes to prevent excessive updates while dragging the color picker
      if (this.colorChangeTimeout) {
        clearTimeout(this.colorChangeTimeout);
      }
      
      this.colorChangeTimeout = setTimeout(() => {
        this.editor.chain().focus().setColor(color).run();
        this.addTextColorToHistory(color);
        this.$nextTick(() => { this.colorMenuOpen = true; });
      }, 100); // 100ms debounce - fast enough to feel responsive, slow enough to reduce updates
    },

    addTextColorToHistory(color) {
      const hex = this.toHex(color);
      if (hex) this.$store.commit('addTextColor', hex);
    },

    toHex(color) {
      if (!color || typeof color !== 'string') return null;
      const m = color.trim().match(/^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})?$/);
      return m ? '#' + m[1].toLowerCase() : null;
    },

    applyRecentColor(hex) {
      this.editor.chain().focus().setColor(hex).run();
      this.$store.commit('addTextColor', hex);
      this.$nextTick(() => { this.colorMenuOpen = true; });
    },

    resetTextColor() {
      this.editor.chain().focus().unsetColor().run();
      this.$nextTick(() => { this.colorMenuOpen = true; });
    },

    setLink() {

      //const previousUrl = this.editor.getAttributes('link').href

      if (!this.url.includes("http")) {
        this.url = '//' + this.url;
      }

      const url = this.url;//window.prompt('URL', previousUrl)

      // cancelled
      if (url === null) {
        return
      }

      // empty
      if (url === '') {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run()

        return
      }

      // update link
      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run()

      this.url = "";
    },

  },

  beforeDestroy() {
    if (this.updateTimeout) clearTimeout(this.updateTimeout)
    if (this.colorChangeTimeout) clearTimeout(this.colorChangeTimeout)
    if (this.closeColorMenuTimeout) clearTimeout(this.closeColorMenuTimeout)
    if (this.selectionUpdateOff) this.selectionUpdateOff()
    if (this.editor) this.editor.destroy()
  },
}
</script>
<style lang="scss">
.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

/* Basic editor styles */
/* .ProseMirror {
  font-size: 20px;
  font-weight: 350;
  padding: 0%;
} */

.editor-link {
  border-color: rgb(255, 190, 10);
  border-bottom-width: 1px;
  border-bottom-style: solid;
  color: inherit !important;
}

a:link {
  text-decoration: none !important;
}

a:visited {
  text-decoration: none !important;
}

a:hover {
  text-decoration: none !important;
}

a:active {
  text-decoration: none !important;
}

[contenteditable]:focus {
  outline: 0px solid transparent;
}

/* Editor Wrapper */
.editor-wrapper {
  position: relative;
}

/* Editor Container */
.tiptap-editor {
  position: relative;
  width: 100%;
}

.tiptap-editor.tiptap-editor--editable:hover {
  background-color: color-mix(in srgb, currentColor 10%, transparent);
}

.editor-content-wrapper {
  position: relative;
}

/* Bubble Menu Styles */
.bubble-menu-container {
  /* Tiptap handles positioning via Tippy.js */
  /* Ensure it's visible */
  display: block !important;
  visibility: visible !important;
}

.bubble-menu-card {
  background: white !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  min-width: fit-content;
  display: inline-block;

  .v-card__text {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 2px;
  }


}

.menu-group {
  display: flex;
  align-items: center;
  gap: 2px;

  /* Active state for all menu buttons (toggle group + link/color buttons) */
  .v-btn.is-active {
    background-color: var(--v-primary-base) !important;
    color: white !important;
  }

  .v-btn.is-active .v-icon {
    color: white !important;
    /* Vuetify icons don't always inherit; force white */
  }

}

.v-divider--vertical {
  height: 32px;
  margin: 0 4px;
}

/* Active state for list items */
.v-list-item--active {
  background-color: rgba(25, 118, 210, 0.12) !important;
  color: #1976d2 !important;
}

/* Text color picker – recent colors */
.text-color-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.color-swatch {
  width: 24px;
  height: 24px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.color-swatch:hover {
  transform: scale(1.1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
</style>